import { HTMLAttributes, PropsWithChildren } from "react";

import { css } from "@compiled/react";

import { wacToken } from "@/libraries/design/foundations";

export type TabPanelProps = PropsWithChildren<{
    isActive?: boolean;
}> &
    HTMLAttributes<HTMLDivElement>;

// All panels are kept in the DOM and stacked in the same CSS grid cell so we can
// cross-fade between them without any layout shift. This matches the canonical
// WAC stacked-cross-fade idiom used in `customer-testimonials` and
// `hero-carousel` (gridArea "1 / 1" + opacity transition). Spec: GWP-11578.
const panelStyles = css({
    "gridArea": "1 / 1",
    "opacity": 0,
    "pointerEvents": "none",
    "transition": `opacity ${wacToken("motion.duration.400ms")} ${wacToken(
        "motion.easing.ease40-in-out"
    )}`,
    // Honor reduced motion: instant swap, no fade.
    "@media (prefers-reduced-motion: reduce)": {
        transition: "none"
    }
});

const activePanelStyles = css({
    opacity: 1,
    pointerEvents: "auto"
});

export const TabPanel = ({
    children,
    id,
    isActive = true,
    ...rest
}: TabPanelProps) => {
    return (
        <div
            css={[panelStyles, isActive && activePanelStyles]}
            id={id}
            role="tabpanel"
            aria-labelledby={`tab-${id}`}
            // Inactive panels are visually invisible (opacity 0) and not
            // interactive (pointer-events: none + tabIndex: -1). They still
            // exist in the accessibility tree so screen readers can navigate,
            // but `aria-hidden` keeps them out of the announcement queue.
            tabIndex={isActive ? 0 : -1}
            aria-hidden={!isActive}
            {...rest}
        >
            {children}
        </div>
    );
};
